import { AppleLogoIcon, GoogleLogoIcon } from "@pathwright/pathicons"
import { TFunction, TOptions } from "i18next"
import { FeatureKey, FeaturePermission } from "../api/generated"

export const featureKeyToParam = (key: string) => key.replace(/_/g, "-")
export const featureParamToKey = (param: string) => param.replace(/-/g, "_")

export type FeatureInfo = ReturnType<typeof getFeatureInfo>

export const getFeatureInfo = (
  featureKey: FeatureKey,
  translator: TFunction
) => {
  switch (featureKey) {
    case FeatureKey.CohortActivityFeed:
      return {
        icon: "activity-feed",
        title: translator("Timeline"),
        desc: translator("Display member activity in Paths."),
        link: "https://help.pathwright.com/en/articles/5360083-cohort-timeline",
        tooltip: translator(
          "A day-by-day view of who's making progress and active in discussion."
        )
      }
    case FeatureKey.CohortMemberDirectory:
      return {
        icon: "avatar-stack",
        title: translator("Member Directory"),
        desc: translator("List members in Paths."),
        link: "https://help.pathwright.com/en/articles/5359893-cohort-member-directory",
        tooltip: translator(
          "A year-book-like view that helps members learn about each other."
        )
      }
    case FeatureKey.AuthLoginOptionGlobalSsoGoogle:
      return {
        icon: <GoogleLogoIcon size="2.5em" />,
        // NOTE: `name` is inconsistent key.
        name: "Google",
        title: translator("Sign in with Google"),
        desc: translator(`Enable members to sign in with Google.`),
        link: "https://help.pathwright.com/en/articles/8260475-enable-faster-sign-in-with-apple-and-google",
        tooltip: translator(
          "Speed up how quickly your members can access your Paths and reduce forgotten password support requests by enabling Sign in with Google."
        )
      }
    case FeatureKey.AuthLoginOptionGlobalSsoApple:
      return {
        icon: <AppleLogoIcon size="2.5em" />,
        // NOTE: `name` is inconsistent key.
        name: "Apple",
        title: translator("Sign in with Apple"),
        desc: translator(`Enable members to sign in with Apple.`),
        link: "https://help.pathwright.com/en/articles/8260475-enable-faster-sign-in-with-apple-and-google",
        tooltip: translator(
          "Speed up how quickly your members can access your Paths and reduce forgotten password support requests by enabling Sign in with Apple."
        )
      }
    case FeatureKey.HomeScreenUnified:
      return {
        icon: "avatar-fill",
        title: translator("Unified Home Screen")
      }
    default:
      return {
        icon: "",
        title: "",
        desc: ""
      }
  }
}

export type FeaturePermissionInfo = ReturnType<typeof getFeaturePermissionInfo>

export const getFeaturePermissionInfo = (
  featureKey: FeatureKey,
  permission: FeaturePermission,
  translator: TFunction,
  tOptions: TOptions<any> = {}
): {
  icon?: string
  title: string
  titleAbbr?: string
  titleVerbose?: string
  desc?: string
  descVerbose?: string
} => {
  switch (featureKey) {
    case FeatureKey.AuthLoginOptionGlobalSsoGoogle:
    case FeatureKey.AuthLoginOptionGlobalSsoApple:
      switch (permission) {
        case FeaturePermission.Disabled:
          return {
            icon: "eye-off",
            title: translator("Disabled"),
            titleAbbr: translator("Disabled"),
            titleVerbose: translator("Disabled"),
            desc: "", // TODO
            descVerbose: "" // TODO
          }
        case FeaturePermission.Everyone:
          return {
            icon: "eye",
            title: translator("Enabled"),
            titleAbbr: translator("Enabled"),
            titleVerbose: translator("Enabled"),
            desc: "", // TODO
            descVerbose: "" // TODO
          }
        default:
          return {
            title: translator("Coming soon"),
            desc: ""
          }
      }
    default:
      switch (permission) {
        case FeaturePermission.Disabled:
          return {
            icon: "eye-off",
            title: translator("Disabled"),
            titleAbbr: translator("Disabled"),
            titleVerbose: translator("Disabled"),
            desc: "", // TODO
            descVerbose: "" // TODO
          }
        case FeaturePermission.AdminOnly:
          return {
            icon: "eye-lock-fill",
            title: translator("Admin only"),
            titleVerbose: translator("Visible to Admin Only"),
            titleAbbr: translator("Admin"),
            desc: translator("Only visible to Administrators."),
            descVerbose: translator(
              "Only Administrators can see the {{ feature }}.",
              tOptions
            )
          }
        case FeaturePermission.StaffOnly:
          return {
            icon: "eye-lock",
            title: translator("Staff only"),
            titleVerbose: translator("Visible to Staff Only"),
            titleAbbr: translator("Staff"),
            desc: translator(
              "Hidden from Learners but visible to Staff roles with access to the cohort."
            ),
            descVerbose: translator(
              "Only Administrators, Editors, Teachers, and Moderators with access to this Cohort can view the {{ feature }}.",
              tOptions
            )
          }
        case FeaturePermission.Everyone:
          return {
            icon: "eye",
            title: translator("Members & Staff"),
            titleVerbose: translator("Visible to Members & Staff"),
            titleAbbr: translator("Everyone"),
            desc: translator("Visible to everyone with access to the cohort."),
            descVerbose: "" // TODO
          }
        default:
          return {
            title: translator("Coming soon"),
            desc: ""
          }
      }
  }
}
